import { EditorState, convertToRaw } from 'draft-js';
import { PRIMARY_DARK, PRIMARY_DARK_ADMIN, BORDER_GRAY } from '../shared/ui/editorAccents';
import { ACCORDION_PANEL_STYLE_SCHEMA } from '../shared/boxes/accordionPanel/accordionPanelProps';
import { INFO_CARD_STYLE_SCHEMA } from '../shared/boxes/infoCard/infoCardProps';
import { classicDefaults } from '../shared/theme/styleSchema';
import { boxPropsFromStyle } from '../shared/boxes/styleTemplate';
import { DEFAULT_TITLE_INDEX_STYLE } from '../components/Title/titleUtils';
import { ORG_CHART_MODES } from './orgChart';
import {
  gridWidth,
  gridHeight,
  casesInGrid,
  calculateWidth,
  calculateLeftPadding,
  calculateHeight,
  navbarHeight,
  contentPadding,
  masksDefaultHeight,
  lineSpacing,
  columnGapPx,
} from './gridConfig';
import {
  faInputText,
  faCheckSquare,
  faListDropdown,
  faRectangleHistoryCirclePlus,
  faCheck as faCheckLight,
  faXmark as faXmarkLight,
  faText,
  faImage,
  faTable,
  faCommentQuestion,
  faOption,
  faFilePdf,
  faObjectGroup,
} from '@fortawesome/pro-light-svg-icons';
import {
  BorderBottomOutlined,
  BorderLeftOutlined,
  BorderRightOutlined,
  BorderTopOutlined,
} from '@ant-design/icons';
import {
  faCheck,
  faXmark,
  faSquare as faSquareRound,
} from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/sharp-solid-svg-icons';
import { faListTree } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { faObjectsColumn } from '@fortawesome/sharp-light-svg-icons';

export const GLOSSARY = {
  // « élément » et non « bloc » : le panneau de gauche appelle élément ce qu'on
  // pose dans un bloc (texte, image, tableau...), et bloc le container qui les
  // porte (rail « Blocs »). Le même mot pour les deux rendait le dialogue
  // trompeur — on ne savait pas si le container partait avec.
  //
  // Pas de branche « maskName » : un masque (nommé ou système : cover, break,
  // newPage...) est cloné avec des ids neufs à l'injection (mutateInjectMask,
  // utils/masks.js) puis ne conserve plus aucun lien vivant avec sa définition
  // d'origine — éditer celle-ci n'affecte jamais les instances déjà injectées
  // ailleurs. Aucun message ne doit donc laisser entendre qu'une suppression
  // se répercute sur d'autres pages.
  REMOVE_BOX: ({ count = 1 } = {}) =>
    count > 1 ? `Supprimer ces ${count} éléments ?` : 'Supprimer cet élément ?',
  GRAS: 'Gras',
  ITALIQUE: 'ITALIC',
  SOULIGNE: 'UNDERLINE',
  LISTE_A_PUCE: 'LISTE_A_PUCE',
  LISTE_NUMEROTEE: 'LISTE_NUMEROTEE',
};

export const colId = () => {
  return `col${uuid()}`;
};
export const rowId = () => {
  return `row${uuid()}`;
};

export const uuid = () => {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  return '_' + Math.random().toString(36).substr(2, 9);
};

export const ROLES_COLORS = {
  superadmin: 'blue',
  admin: 'red',
  setup: 'orange',
  salesman: 'gold',
  manager: 'gold',
  supervisor: 'gold',
};

// export const CASE = {
//   height: 35.093976378369497,
//   width: 24.8031496063,
// };
// export const styleCheckbox = {
//    validate: "validate-box",
//    cross: "cross-box"
// }
export const styleCheckbox = {
  validate_checkbox: faCheck,
  validate_checkbox_light: faCheckLight,
  cross_checkbox: faXmark,
  cross_checkbox_light: faXmarkLight,
  square_checkbox: faSquare,
  square_checkbox_round: faSquareRound,
};

// Tableaux dont la colonne image (Planning) est générée en asynchrone par le
// microservice screenshot : cellule vide => génération en cours (loader Smart4),
// re-fetch déclenché par le broadcast WS planning_screenshots_ready.
export const PLANNING_SCREENSHOT_TABLES = [
  // Entrées génériques (« 1 variable + Drawer ») : ce sont elles que porte
  // box.tableName depuis que le backend replie les variantes V/H (collapse_list).
  // Sans elles ici, une box planning récente n'avait ni régénération au fetch, ni
  // loader de cellule, ni rafraîchissement au broadcast WS.
  'DISPOSITIF_PLANNING',
  'DISPOSITIF_PLANNING_PROFIL',
  'DISPOSITIF_PLANNING_SPECIAL',
  // Variantes historiques : conservées pour les documents déjà enregistrés.
  'WORK_POSITIONS_PLANNINGS_VERTICAL',
  'WORK_POSITIONS_PLANNINGS_HORIZONTAL',
  'WORK_POSITIONS_PROFIL_PLANNINGS_VERTICAL',
  'WORK_POSITIONS_PROFIL_PLANNINGS_HORIZONTAL',
  'WORK_POSITIONS_SPECIAL_PLANNINGS_HORIZONTAL',
  // Legacy alias — même rendu horizontal ; conservé pour docs existants.
  'WORK_POSITIONS_SPECIAL_PLANNINGS_VERTICAL',
];

// Images variables planning en section récursive work_position : masquées si pas
// de data (même logique que tableaux sans rows_<poste.id>).
export const RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS = [
  'planning_vertical',
  'planning_horizontal',
  'special_planning_vertical',
  'special_planning_horizontal',
];

export const TitlesTypes = {
  TITLE: 'title1',
  TITLE_2: 'title2',
  TITLE_3: 'title3',
  TITLE_4: 'title4',
};

// A déplacer dans I18N
export const TitlesName = {
  title1: 'Titre 1',
  title2: 'Titre 2',
  title3: 'Titre 3',
  title4: 'Titre 4',
};

export const TITLES = [
  { id: 'title1', name: 'Titre 1' },
  { id: 'title2', name: 'Titre 2' },
  { id: 'title3', name: 'Titre 3' },
  { id: 'title4', name: 'Titre 4' },
];

// « Ligne » est proposée dans le même sélecteur que les formes : côté
// utilisateur il n'y a plus qu'un élément « Forme », la ligne en est une
// variante. Elle reste un type de box à part (ItemTypes.LINE, rendue en CSS et
// non en canvas Konva, pour ne rien changer aux documents existants) : le
// sélecteur convertit la box d'un type à l'autre (cf. mutateSetShapeKind).
export const SHAPE_KIND_LINE = 'line';

export const SHAPES = [
  { id: 'square', name: 'Square' },
  // Juste après le carré : c'est l'une des deux formes les plus posées, elle
  // n'a rien à faire en fin de liste.
  { id: SHAPE_KIND_LINE, name: 'Line' },
  { id: 'round', name: 'Circle' },
  { id: 'rectangle', name: 'Rectangle' },
  { id: 'star', name: 'Star' },
  { id: 'ellipse', name: 'Ellipse' },
  { id: 'ring', name: 'Ring' },
  { id: 'triangle', name: 'Triangle' },
  { id: 'hexagone', name: 'Hexagone' },
  // { id: 'diamond', name: 'Diamond' },
  { id: 'arrow', name: 'Arrow' },
];

export const SPACE_BOX_SIZE = [
  { id: 'small', name: 'Small' },
  { id: 'medium', name: 'Medium' },
  { id: 'large', name: 'Large' },
];

export const columnDefaultDisposition = {
  1: [1],
  2: [0.5, 0.5],
  3: [0.33, 0.33, 0.33],
  4: [0.25, 0.25, 0.25, 0.25],
};

export const ItemTypes = {
  TEXT: 'text',
  IMG: 'img',
  SHAPE: 'shape',
  LINE: 'line',
  TABLE: 'table',
  FILE_PDF: 'file_pdf',
  FILE_PDF_VARIABLE: 'file_pdf_variable',
  SPACE: 'space',
  ...TitlesTypes,
  SUMMARY: 'summary',
  MASK: 'mask',
  DRAW: 'draw',
  SECTION: 'section',
  SECTION_LINK: 'section_link',
  ORG_CHART: 'org_chart',
  LINE_BREAK: 'line_break',
  CHECKBOXS_VARIABLE: 'checkboxs_variable',
  IMG_VARIABLE: 'img_variable',
  TABLE_VARIABLE: 'table_variable',
  GROUP_BOXES: 'group_boxes',
  TEXT_VARIABLE: 'text_variable',
  SELECTOR_VARIABLE: 'selector_variable',
  SECTION_VARIABLE: 'section_variable',
  CUSTOM_TEXT_VARIABLE: 'custom_text_variable',
  VARIABLE_GROUP: 'variable_group',
  QUESTION: 'question',
  CONTAINER_COPIED: 'container_copied',
  // Disposition prête à l'emploi (cf. constants/containerTemplates.js). Type
  // de glisser-déposer à part entière : contrairement à CONTAINER_COPIED, la
  // charge transportée est un simple identifiant, pas un conteneur sérialisé.
  CONTAINER_TEMPLATE: 'container_template',
  RECURSIVE_SECTION: 'recursive_section',
  RECURSIVE_MODEL: 'recursive_model',
  // Box partagées avec l'éditeur de site : le parser les convertit en vraies
  // box web (docs/webviewer/chantier-boxes-partagees.md).
  ACCORDION_PANEL: 'accordion_panel',
  INFO_CARD: 'info_card',
};

export const TriggerableTypes = [
  ItemTypes.QUESTION,
  ItemTypes.CHECKBOXS_VARIABLE,
  ItemTypes.SELECTOR_VARIABLE,
  ItemTypes.SECTION_VARIABLE,
  ItemTypes.CUSTOM_TEXT_VARIABLE,
  'optional-zone',
  'zone-name',
  'framework-agreement-opportunity',
];

export const ItemTypesForTraduction = {
  text: 'text',
  img: 'image',
  shape: 'shape',
  line: 'line',
  table: 'table',
  title1: 'title1',
  title2: 'title2',
  title3: 'title3',
  title4: 'title4',
  summary: 'summary',
  line_break: 'line_break',
  checkboxs_variable: 'checkboxs_variable',
  selector_variable: 'selector_variable',
  text_variable: 'text_variable',
  img_variable: 'img_variable',
  table_variable: 'table_variable',
  container_copied: 'container_copied',
  space: 'space',
  group_boxes: 'group_boxes',
  org_chart: 'org_chart',
  accordion_panel: 'accordion_panel',
  info_card: 'info_card',
};

//Custom overrides for "code" style.
export const COLOR_STYLE_MAP = {
  white: {
    color: 'rgba(255,255,255, 1.0)',
  },
  black: {
    color: 'rgba(0, 0, 0, 1.0)',
  },
  transparent: {
    color: 'transparent',
  },
};

export const HIGHTLIGHT_COLOR_STYLE_MAP = {
  yellow: {
    color: '#faed27',
  },
  blue: {
    color: 'rgba(40, 143, 227, 0.43)',
  },
  transparent: {
    color: 'transparent',
  },
};

export const COLORS = Object.keys(COLOR_STYLE_MAP).map((k) => ({
  label: k,
  style: COLOR_STYLE_MAP[k].color,
}));

export const hightlightColorList = Object.keys(HIGHTLIGHT_COLOR_STYLE_MAP).map(
  (k) => ({
    label: k,
    style: HIGHTLIGHT_COLOR_STYLE_MAP[k].color,
  })
);

export const defaultTableConfiguration = {
  id: 0,
  name: 'Défaut',
  bordered: true,
  footer: false,
  footerData: '',
  showHeader: true,
  size: 'default',
  stripped1: false,
  strippedCustomClrBool: false,
  strippedCustomClr1: COLORS.find((color) => color.label === 'white'),
  strippedCustomClr2: COLORS.find((color) => color.label === 'black'),
  customTextColorBool: false,
  customTextColor1: COLORS.find((color) => color.label === 'black'),
  customTextColor2: COLORS.find((color) => color.label === 'black'),
  // Zébré : le texte des lignes paires prend customTextColor2. Faux pour les
  // styles d'avant, dont customTextColor2 n'a jamais été choisi.
  alternateTextColors: false,
  customCellColorBool: false,
  customCellColor: COLORS.find((color) => color.label === 'white'),
  customHeaderColor: COLORS.find((color) => color.label === 'white'),
  customHeaderTextColor: COLORS.find((color) => color.label === 'black'),
  customBorderBool: false,
  customBorderWidth: '',
  customBorderType: '',
  customBorderColor: COLORS.find((color) => color.label === 'black'),
  borderHorizontalOnly: false,
};
// Registre des tableaux paramétrables : déplacé dans `src/shared/tables/`, le
// site web en a besoin sans traîner tout `constants.js`. Réexporté ici pour que
// les appelants historiques ne bougent pas.
export {
  TABLE_CONFIG_DESCRIPTORS,
  CONFIGURABLE_TABLE_NAMES,
  isConfigurableTable,
  getTableConfigControls,
  isTableColumnsConfigurable,
  getTableConfigLabelKeys,
  tableTitleWithOptions,
  getTablePreviewLayout,
  getTablePreviewSample,
  defaultTableConfig,
  tableConfigDataAxisChanged,
  tableConfigForSplitScope,
} from '../shared/tables/tableDescriptors';

export const defaultBorderTextVariable = {
  color: {
    label: 'transparent',
    style: 'rgba(255, 255, 255, 0.0)',
  },
  radius: 0,
  size: 1,
  left: false,
  right: false,
  top: false,
  bottom: false,
};

export const defaultTextConfiguration = {
  id: 0,
  name: 'Défaut',
  positions: { horizontal: 'left', vertical: 'center' },
  imagePosition: 'center',
  zIndex: 0,
  paddinngLeftRight: 0,
  paddingTopBottom: 0,
  fontFamily: 'Montserrat',
  fontSize: 14,
  color: COLORS.find((color) => color.label === 'black'),
  fontWeight: '',
  fontStyle: '',
  textDecoration: '',
  width: gridWidth * 12 * (casesInGrid / 32),
  textStyleId: 0,
  border: defaultBorderTextVariable,
  backgroundColor: COLORS.find((color) => color.label === 'transparent'),
};

export const defaultSelectorConfiguration = {
  id: 0,
  name: 'Défaut',
  positions: { horizontal: 'start', vertical: 'center' },
  zIndex: 0,
  width: gridWidth * 12 * (casesInGrid / 32),
  content: null,
  fontFamily: 'Montserrat',
  height: 'auto',
  fontSize: 14,
  position: 'start',
  separator: '-',
  backgroundColor: COLORS.find((color) => color.label === 'transparent'),
  borderRadius: 0,
  borderColor: COLORS.find((color) => color.label === 'transparent'),
  color: COLORS.find((color) => color.label === 'black'),
  fontWeight: '',
  bordersSize: 1,
  paddingTopBottom: 0,
  paddingLeftRight: 0,
};

export const defaultCheckboxConfiguration = {
  id: 0,
  name: 'Défaut',
  fontWeight: '',
  fontStyle: '',
  textDecoration: '',
  fontSize: 14,
  fontFamily: 'Montserrat',
  styleBox: 'validate_checkbox',
  box: {
    width: 20,
    height: 20,
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
  },
  label: {
    margin: 8,
    direction: 'ltr',
  },
  check: {
    color: COLORS.find((color) => color.label === 'black'),
    width: 18,
    height: 18,
    weight: 400,
  },
  color: COLORS.find((color) => color.label === 'black'),
  backgroundColor: COLORS.find((color) => color.label === 'transparent'),
  borders: {
    radius: 1,
    size: 1,
    color: COLORS.find((color) => color.label === 'black'),
    top: false,
    bottom: false,
    left: false,
    right: false,
  },
  rowGap: lineSpacing,
  columnGap: 8,
  disposition: 2,
};

/**
 * Modèle de Volet — défini dans le configurateur du template. Appliqué à une
 * box, il y est RECOPIÉ : la box devient autonome et se personnalise ensuite
 * dans le panel de droite.
 *
 * Les props communes y portent LES NOMS DU BLOC WEB (couleurs en CSS, pas en
 * objets de palette) : le parser les recopie telles quelles dans le site.
 * Cf. docs/webviewer/chantier-boxes-partagees.md.
 */
export const defaultAccordionPanelConfiguration = {
  id: 0,
  name: 'Défaut',
  ...classicDefaults(ACCORDION_PANEL_STYLE_SCHEMA),
};

/**
 * Modèle de Carte info — défini dans le configurateur du template. Appliqué à
 * une box, il y est RECOPIÉ : la box devient autonome et se personnalise
 * ensuite dans le panel de droite.
 *
 * Les props communes y portent LES NOMS DU BLOC WEB (couleurs en CSS) : le
 * parser les recopie telles quelles dans le site. Les deux couleurs de texte,
 * elles, ne valent que dans le document.
 * Cf. docs/webviewer/chantier-boxes-partagees.md.
 */
export const defaultInfoCardConfiguration = {
  id: 0,
  name: 'Défaut',
  ...classicDefaults(INFO_CARD_STYLE_SCHEMA),
  /**
   * Texte de démonstration du modèle. Posé quand le style est semé depuis un
   * modèle de la galerie du site, il sert à l'aperçu du configurateur et
   * remplace le texte de démonstration d'une carte à laquelle on applique ce
   * style — « 98 % / Clients satisfaits » sur un témoignage ne veut rien dire.
   * Jamais envoyé sur le site : le contenu réel de la carte y va.
   */
  sample: { value: '98 %', description: 'Clients satisfaits' },
};

export const DefaultBorderType = ['dotted', 'dashed', 'solid', 'none'];

export const DefaultBorderTypeTranslation = {
  dotted: 'A pois',
  dashed: 'Pointillés',
  solid: 'Ligne',
  none: 'Aucune',
};

function roundToNearestStep(value, step) {
  return Math.round(value / step) * step;
}
export const zoomList = [
  { value: 25, label: '25 %', index: 0 },
  { value: 33, label: '33 %', index: 1 },
  { value: 50, label: '50 %', index: 2 },
  { value: 67, label: '67 %', index: 3 },
  { value: 80, label: '80 %', index: 4 },
  { value: 90, label: '90 %', index: 5 },
  { value: 100, label: '100 %', index: 6 },
  { value: 110, label: '110 %', index: 7 },
  { value: 125, label: '125 %', index: 8 },
  { value: 150, label: '150 %', index: 9 },
  { value: 175, label: '175 %', index: 10 },
  { value: 200, label: '200 %', index: 11 },
  { value: 250, label: '250 %', index: 12 },
  { value: 300, label: '300 %', index: 13 },
  { value: 400, label: '400 %', index: 14 },
  { value: 500, label: '500 %', index: 15 },
];

// Point de référence par orientation pour le pourcentage de zoom affiché : à
// 100 % (choix ou reload), l'échelle CSS réellement appliquée est celle qui
// "rend bien" visuellement pour cette orientation (portrait 90 %, paysage
// 67 %), pas un 1:1 brut. Cf. utils/zoom.js::effectiveZoom.
export const zoomOrientationReference = {
  portrait: 90,
  landscape: 67,
};

export const dynamicZoom = () => {
  const arrayZoom = {
    1100: zoomList.find((z) => z.index === 3).value,
    1200: zoomList.find((z) => z.index === 4).value,
    1300: zoomList.find((z) => z.index === 5).value,
  };
  return arrayZoom?.[roundToNearestStep(window.innerWidth, 100)];
};

export const defaultZoom = 100;

export const defaultConfiguration = {
  fontFamily: 'Montserrat', // should be the key of default font
  fontFamilyList: [{ key: 'Montserrat', familyName: 'Montserrat' }], // List of objects
  fontSize: 14,
  baseFontColor: { label: 'black', style: 'rgba(0,0,0,1)' },
  colors: COLORS,
  highlightColors: hightlightColorList,
  currentHighlightColor: {
    label: 'yellow',
    style: '#faed27',
  },
  margins: {
    left: 8,
    right: 8,
    top: 4,
    bottom: 4,
  },
  lineSpacing: 5,
  sizeSpaceContainer: [
    { id: 'small', name: 'Small', value: 15 },
    { id: 'medium', name: 'Medium', value: 30 },
    { id: 'large', name: 'Large', value: 60 },
  ],
  defaultSizeSpaceContainer: 'small',
  columnGap: columnGapPx,
  customBulletPadding: '0px 0px 0px 0px',
  indentationTitles: 0,
  editableTemplate: true,
  unlockProposal: true,
  separatorTitle: '.',
  // Séparateur après le dernier numéro de l'index ("1.2." plutôt que "1.2").
  finalSeparatorTitle: true,
  shapeColor: COLORS.find((color) => color.label === 'black'),
  // Ancien réglage des niveaux masqués, gardé pour les documents qui n'ont pas
  // encore les cases par niveau (cf. numberedTitleLevels).
  //
  // Pas de `numberedTitleLevels` par défaut, volontairement : la configuration
  // chargée est fusionnée avec ces valeurs (syncStructure), et un tableau ici
  // gagnerait sur le réglage des documents d'avant, qui se remettraient tous à
  // numéroter tous les niveaux. Champ absent = ce que dit hideTitleIndex.
  hideTitleIndex: 0,
  pageNumberStyle: {
    // 'footer' | 'header'
    placement: 'footer',
    // Vide : Helvetica, la police historique du numéro dans le PDF.
    fontFamily: '',
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    size: 14,
    color: COLORS.find((color) => color.label === 'black'),
    format: 'simple',
    position: {
      right: 17,
      bottom: 17,
      // Écart avec le haut de la page quand le numéro est dans l'en-tête.
      top: 17,
    },
  },
  // Filigrane : texte en diagonale sur les pages du PDF (et de son aperçu).
  watermark: {
    enabled: false,
    text: '',
    // Vide : Helvetica, comme le numéro de page.
    fontFamily: '',
    fontWeight: 'bold',
    size: 100,
    color: COLORS.find((color) => color.label === 'black'),
    // De 0 à 1, appliquée en plus de celle de la couleur.
    opacity: 0.15,
    // 'all' | 'content' (sans les pages pleine page : couverture, séparateurs,
    // PDF importés en pleine page)
    pages: 'all',
    // 'always' | 'missingValues' (tant que le formulaire de la proposition a
    // des valeurs manquantes, comme le point d'exclamation)
    display: 'always',
    // Le PDF archivé est la version officielle : pas de « BROUILLON » dessus.
    hideOnArchive: true,
  },
  showHeaderOnPages: 'all',
  showFooterOnPages: 'all',
  displayChapterSeparation: true,
  textStyling: {
    textItalic: false,
    textBold: false,
    textUnderline: false,
    textColor: {},
    textHighlightColor: '',
    textAlign: '',
    textListType: '',
  },
  text_variable: {
    textItalic: false,
    textBold: false,
    textUnderline: false,
    textColor: {},
    textHighlightColor: '',
    textAlign: '',
    textListType: '',
  },
  tableStyles: [{ ...defaultTableConfiguration }],
  checkboxStyles: [{ ...defaultCheckboxConfiguration }],
  accordionPanelStyles: [{ ...defaultAccordionPanelConfiguration }],
  infoCardStyles: [{ ...defaultInfoCardConfiguration }],
  selectorStyles: [{ ...defaultSelectorConfiguration }],
  textStyles: [{ ...defaultTextConfiguration }],
  tableStylesIdCount: 1,
  checkboxStylesIdCount: 1,
  accordionPanelStylesIdCount: 1,
  infoCardStylesIdCount: 1,
  selectorStylesIdCount: 1,
  textStylesIdCount: 1,
  unorderedListBulletValue0: 'Defaut',
  unorderedListBulletValue1: 'Defaut',
  unorderedListBulletValue2: 'Defaut',
  unorderedListBulletValue3: 'Defaut',
  unorderedListBulletValue4: 'Defaut',
  unorderedListBulletColor0: COLORS.find((color) => color.label === 'transparent'),
  unorderedListBulletColor1: COLORS.find((color) => color.label === 'transparent'),
  unorderedListBulletColor2: COLORS.find((color) => color.label === 'transparent'),
  unorderedListBulletColor3: COLORS.find((color) => color.label === 'transparent'),
  unorderedListBulletColor4: COLORS.find((color) => color.label === 'transparent'),

  title1: {
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    textAlign: 'left',
    fontSize: 22,
    fontFamily: '',
    marginTop: 0,
    marginBottom: 0,
    color: COLORS.find((color) => color.label === 'black'),
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
    typeTitle: 'chiffre',
    indexTitle: '',
    // Disposition numéro / texte (cf. getTitleLayout).
    verticalAlignment: 'baseline',
    textOffset: 0,
    indexStyle: { ...DEFAULT_TITLE_INDEX_STYLE },
    borders: {
      size: 3,
      color: COLORS.find((color) => color.label === 'transparent'),
      top: false,
      bottom: false,
      left: false,
      right: false,
    },
  },
  title2: {
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    textAlign: 'left',
    fontSize: 20,
    fontFamily: '',
    marginTop: 0,
    marginBottom: 0,
    color: COLORS.find((color) => color.label === 'black'),
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
    typeTitle: 'chiffre',
    indexTitle: '',
    // Disposition numéro / texte (cf. getTitleLayout).
    verticalAlignment: 'baseline',
    textOffset: 0,
    indexStyle: { ...DEFAULT_TITLE_INDEX_STYLE },
    borders: {
      size: 3,
      color: COLORS.find((color) => color.label === 'transparent'),
      top: false,
      bottom: false,
      left: false,
      right: false,
    },
  },
  title3: {
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    textAlign: 'left',
    fontSize: 18,
    fontFamily: '',
    marginTop: 0,
    marginBottom: 0,
    color: COLORS.find((color) => color.label === 'black'),
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
    typeTitle: 'chiffre',
    indexTitle: '',
    // Disposition numéro / texte (cf. getTitleLayout).
    verticalAlignment: 'baseline',
    textOffset: 0,
    indexStyle: { ...DEFAULT_TITLE_INDEX_STYLE },
    borders: {
      size: 3,
      color: COLORS.find((color) => color.label === 'transparent'),
      top: false,
      bottom: false,
      left: false,
      right: false,
    },
  },
  title4: {
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    textAlign: 'left',
    fontSize: 16,
    fontFamily: '',
    marginTop: 0,
    marginBottom: 0,
    color: COLORS.find((color) => color.label === 'black'),
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
    typeTitle: 'chiffre',
    indexTitle: '',
    // Disposition numéro / texte (cf. getTitleLayout).
    verticalAlignment: 'baseline',
    textOffset: 0,
    indexStyle: { ...DEFAULT_TITLE_INDEX_STYLE },
    borders: {
      size: 3,
      color: COLORS.find((color) => color.label === 'transparent'),
      top: false,
      bottom: false,
      left: false,
      right: false,
    },
  },
  checkboxs_variable: {
    fontWeight: '',
    fontStyle: '',
    textDecoration: '',
    fontSize: 14,
    fontFamily: '',
    color: COLORS.find((color) => color.label === 'black'),
    backgroundColor: COLORS.find((color) => color.label === 'transparent'),
    borders: {
      size: 3,
      color: COLORS.find((color) => color.label === 'transparent'),
      top: false,
      bottom: false,
      left: false,
      right: false,
    },
    rowGap: lineSpacing,
    columnGap: 8,
    disposition: 2,
  },
  defaultDocumentView: 'document',
  dmz: 102,
};

export const defaultPageStructure = {
  id: '',
  masks: [],
  backgroundColor: {
    label: 'white',
    style: 'rgba(255, 255, 255, 1.0)',
  },
};

export const defaultBorder = {
  color: {
    label: 'transparent',
    style: 'rgba(255, 255, 255, 0.0)',
  },
  size: 3,
  left: false,
  right: false,
  top: false,
  bottom: false,
};

export const defaultBackground = {
  label: 'transparent',
  style: 'rgba(255, 255, 255, 0.0)',
};

export const defaultImgAspectRatio = 512 / 478.0; // from images/placeholder-img.png

export const AUTO_HEIGHT_BOXES = [
  'text',
  'img',
  ...Object.values(TitlesTypes),
  'summary',
  'table',
  ItemTypes.SELECTOR_VARIABLE,
  ItemTypes.TEXT_VARIABLE,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TABLE_VARIABLE,
  ItemTypes.ORG_CHART,
];

export const checkboxDispositions = {
  ONE_COLUMN: 1,
  TWO_COLUMNS: 2,
  THREE_COLUMNS: 3,
  FOUR_COLUMNS: 4,
};

const defaultNewTableWidth = calculateWidth(
  defaultConfiguration.margins,
  defaultConfiguration.indentationTitles,
  0
);
const defaultNewTableHalfColWidth = Math.floor(defaultNewTableWidth / 2);

const baseTable = {
  positions: { horizontal: 'center', vertical: 'center' },
  zIndex: 0,
  variablePath: '',
  width: defaultNewTableWidth,
  height: 'auto',
  left: calculateLeftPadding(
    defaultConfiguration.margins,
    defaultConfiguration.indentationTitles,
    0
  ),
  content: {
    columns: [
      {
        key: colId(),
        title: 'Col A',
        width: defaultNewTableHalfColWidth,
      },
      {
        key: colId(),
        title: 'Col B',
        width: defaultNewTableWidth - defaultNewTableHalfColWidth,
      },
    ],
    columnsDisposition: [],
    data: [
      {
        hiddenColumnsKeys: [],
        key: rowId(),
      },
    ],
  },
};

const baseImg = {
  positions: { horizontal: 'center', vertical: 'center' },
  zIndex: 0,
  variablePath: '',
  width: gridWidth * 6 * (casesInGrid / 32),
  height: 'auto',
  content: {
    src: null,
  },
  border: defaultBorder,
  backgroundColor: defaultBackground,
};

export const defaultItemValues = {
  summary: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: calculateWidth(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      0
    ),
    height: gridHeight * 12,
    left: calculateLeftPadding(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      0
    ),
    paddingSummary: 12,
    content: {
      configuration: {
        fontFamily: '',
        fontSize: 14,
        colors: COLORS,
        lineSummaryStart: null,
        lineSummaryEnd: null,
        // Le numéro reprend le style de celui des titres du document
        // (couleur, opacité, contour, graisse) au lieu de celui de sa ligne de
        // sommaire. Réglage du document entier, cf. summaryIndexStyle.
        indexStyleFromTitles: false,
        // Retrait de chaque niveau de sous-titre par rapport au précédent, en px.
        indentation: 15,
        table: { ...defaultTableConfiguration },
        title1: {
          visibility: 'visible',
          fontSize: 22,
          fontFamily: '',
          fontWeight: '',
          fontStyle: '',
          textDecoration: '',
          marginTop: 0,
          marginBottom: 0,
          skipLineAfter: false,
          showPageNumber: true,
          // Liaison entre le titre et son n° de page : 'none', 'dotted', 'solid'.
          pageLeader: 'none',
          color: COLORS.find((color) => color.label === 'black'),
        },
        title2: {
          visibility: 'visible',
          fontSize: 18,
          fontFamily: '',
          fontWeight: '',
          fontStyle: '',
          textDecoration: '',
          marginTop: 0,
          marginBottom: 0,
          skipLineAfter: false,
          showPageNumber: true,
          pageLeader: 'none',
          color: COLORS.find((color) => color.label === 'black'),
        },
        title3: {
          visibility: '',
          fontSize: 14,
          fontFamily: '',
          fontWeight: '',
          fontStyle: '',
          textDecoration: '',
          marginTop: 0,
          marginBottom: 0,
          skipLineAfter: false,
          showPageNumber: true,
          pageLeader: 'none',
          color: COLORS.find((color) => color.label === 'black'),
        },
        title4: {
          visibility: '',
          fontSize: 10,
          fontFamily: '',
          fontWeight: '',
          fontStyle: '',
          textDecoration: '',
          marginTop: 0,
          marginBottom: 0,
          skipLineAfter: false,
          showPageNumber: true,
          pageLeader: 'none',
          color: COLORS.find((color) => color.label === 'black'),
        },
      },
    },
  },
  title1: {
    positions: { horizontal: 'end', vertical: 'center' },
    zIndex: 0,
    paddingTitle: 12,
    width: calculateWidth(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      0
    ),
    height: calculateHeight(),
    left: calculateLeftPadding(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      0
    ),
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
      ...defaultConfiguration.title1,
    },
    border: defaultBorder,
    backgroundColor: defaultBackground,
  },
  title2: {
    positions: { horizontal: 'end', vertical: 'center' },
    zIndex: 0,
    paddingTitle: 12,
    width: calculateWidth(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      1
    ),
    height: calculateHeight(),
    left: calculateLeftPadding(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      1
    ),
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
      ...defaultConfiguration.title2,
    },
    border: defaultBorder,
    backgroundColor: defaultBackground,
    },
  title3: {
    positions: { horizontal: 'end', vertical: 'center' },
    zIndex: 0,
    paddingTitle: 12,
    width: calculateWidth(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      2
    ),
    height: calculateHeight(),
    left: calculateLeftPadding(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      2
    ),
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
      ...defaultConfiguration.title3,
    },
    border: defaultBorder,
    backgroundColor: defaultBackground,
    },
  title4: {
    positions: { horizontal: 'end', vertical: 'center' },
    zIndex: 0,
    paddingTitle: 12,
    width: calculateWidth(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      3
    ),
    height: calculateHeight(),
    left: calculateLeftPadding(
      defaultConfiguration.margins,
      defaultConfiguration.indentationTitles,
      3
    ),
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
      ...defaultConfiguration.title4,
    },
    border: defaultBorder,
    backgroundColor: defaultBackground,
    },
  text: {
    positions: { horizontal: 'start', vertical: 'start' },
    zIndex: 0,
    textStyleId: 0,
    width: gridWidth * 12 * (casesInGrid / 32),
    height: gridHeight * (casesInGrid / 32),
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
    },
    border: defaultBorder,
    backgroundColor: defaultBackground,
    },
  text_variable: {
    positions: { horizontal: 'start', vertical: 'start' },
    zIndex: 0,
    width: gridWidth * 12 * (casesInGrid / 32),
    height: gridHeight * (casesInGrid / 32),
    textStyleId: 0,
    content: {
      text: '',
      editorState: EditorState.createEmpty(),
    },
    value_required: false,
    remark: JSON.stringify(
      convertToRaw(EditorState.createEmpty().getCurrentContent())
    ),
  },
  shape: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: gridWidth * 3 * (casesInGrid / 32),
    height: gridWidth * 3 * (casesInGrid / 32),
    content: {
      color: 'rgba(26, 188, 156, 1)',
      // Rectangle et non carré : le carré verrouille les proportions (cf.
      // LOCK_PROPORTIONS_SHAPES), il fallait donc changer de type avant même de
      // pouvoir étirer la forme qu'on venait de poser.
      shape: 'rectangle',
    },
    rotation: 0,
    radius: 0,
    shadow: {
      enabled: false,
      color: COLORS.find((color) => color.label === 'black'),
      blur: 6,
      offsetX: 4,
      offsetY: 4,
      opacity: 0.5,
    },
    borders: {
      color: {
        label: 'transparent',
        style: 'rgba(255, 255, 255, 0.0)',
      },
      size: 0,
    },
    arrows: {
      right: true,
      left: false,
      stroke: 5,
    },
    stroke: 5,
  },
  line: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: gridWidth * 24 * (casesInGrid / 32),
    // Épaisseur du trait : 10px faisait une barre, pas un filet. Les lignes
    // déjà posées gardent la leur (elle est stockée sur la box).
    height: 5,
    content: {
      color: 'rgba(26, 188, 156, 1)',
      style: 'solid',
      height: 5,
      side: 'Bottom',
    },
  },
  img: baseImg,
  table: baseTable,
  mask: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    content: null,
    border: defaultBorder,
    backgroundColor: 'rgba(20, 20, 20, 0.3)',
  },
  line_break: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    content: null,
    height: 'auto',
  },
  recursive_section: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    height: 'auto',
    content: {
      childTemplateId: null,
      childTemplateName: null,
      childModelName: null,
    },
  },
  recursive_model: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    height: 'auto',
    content: {
      forcedTemplates: {},
    },
  },
  checkboxs_variable: {
    positions: { horizontal: 'start', vertical: 'center' },
    zIndex: 0,
    width: 200,
    content: null,
    height: 'auto',
    name: '',
    maxNumberOfAnwser: 1,
    minNumberOfAnwser: 1,
    onlyOneRequiredAnswer: true,
    checkboxStyleId: 0,
    checkboxs: [],
    onlyForm: false,
    remark: JSON.stringify(
      convertToRaw(EditorState.createEmpty().getCurrentContent())
    ),
  },
  selector_variable: {
    positions: { horizontal: 'start', vertical: 'center' },
    zIndex: 0,
    width: 200,
    content: null,
    height: 'auto',
    name: '',
    maxNumberOfAnwser: 1,
    minNumberOfAnwser: 1,
    onlyOneRequiredAnswer: true,
    selectorStyleId: 0,
    options: [],
    remark: JSON.stringify(
      convertToRaw(EditorState.createEmpty().getCurrentContent())
    ),
  },
  section_variable: {
    positions: { horizontal: 'start', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    content: null,
    height: 'auto',
    name: '',
    answer: null,
    sectionId: null,
    questions: [
      {
        label: 'Oui',
        id: uuid(),
      },
      {
        label: 'Non',
        id: uuid(),
      },
    ],
    remark: JSON.stringify(
      convertToRaw(EditorState.createEmpty().getCurrentContent())
    ),
  },
  img_variable: baseImg,
  table_variable: baseTable,
  group_boxes: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: 'auto',
    height: 'auto',
    rotation: 0,
    boxIds: [],
  },
  // Volet : hauteur pilotée par le contenu (les deux textes grandissent), et
  // le sac de style aux noms de props du bloc web — cf.
  // shared/boxes/accordionPanel.
  accordion_panel: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: gridWidth * 24 * (casesInGrid / 32),
    height: 'auto',
    content: {
      questionEditorState: EditorState.createEmpty(),
      answerEditorState: EditorState.createEmpty(),
      // Cf. la Carte info : le style du template est un modèle recopié ici.
      panel: boxPropsFromStyle(defaultAccordionPanelConfiguration),
    },
  },
  // Carte info : hauteur pilotée par le contenu, et style porté par le
  // template — cf. shared/boxes/infoCard.
  info_card: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: gridWidth * 12 * (casesInGrid / 32),
    height: 'auto',
    content: {
      titleEditorState: EditorState.createEmpty(),
      descriptionEditorState: EditorState.createEmpty(),
      // La box porte SES valeurs : un style du template n'est qu'un modèle,
      // recopié ici au moment où on l'applique. Tout se personnalise ensuite
      // dans le panel de droite.
      card: boxPropsFromStyle(defaultInfoCardConfiguration),
    },
  },
  org_chart: {
    positions: { horizontal: 'center', vertical: 'center' },
    zIndex: 0,
    width: gridWidth * 6 * (casesInGrid / 32),
    height: 'auto',
    orgChartId: null,
    orgChartSourceId: null,
    // Comportement à l'injection, réglé depuis le modèle (cf. constants/orgChart).
    orgChartMode: ORG_CHART_MODES.CHOOSE,
    orgChartUnlocked: false,
    content: { imageUrl: null, name: null, ratio: null },
    border: defaultBorder,
    backgroundColor: defaultBackground,
  },
};

export const variablesIcons = {
  [ItemTypes.CHECKBOXS_VARIABLE]: faCheckSquare,
  [ItemTypes.TEXT_VARIABLE]: faInputText,
  [ItemTypes.SELECTOR_VARIABLE]: faListDropdown,
  [ItemTypes.SECTION_VARIABLE]: faRectangleHistoryCirclePlus,
  [ItemTypes.CUSTOM_TEXT_VARIABLE]: faText,
  [ItemTypes.VARIABLE_GROUP]: faListTree,
  [ItemTypes.IMG_VARIABLE]: faImage,
  [ItemTypes.TABLE_VARIABLE]: faTable,
  [ItemTypes.QUESTION]: faCommentQuestion,
  'optional-zone': faOption,
  [ItemTypes.CONTAINER_COPIED]: faObjectsColumn,
  'zone-name': faOption,
  'framework-agreement-opportunity': faOption,
  [ItemTypes.FILE_PDF_VARIABLE]: faFilePdf,
  [ItemTypes.GROUP_BOXES]: faObjectGroup,
};

export const defaultColumnWidth = 50;

export const defaultSlider = {
  from: {
    value: null,
  },
  to: {
    value: null,
  },
  boxes: [],
};

export const UNMASKABLE_TYPES = [...Object.values(TitlesTypes), 'summary'];

export const canBeInMask = (boxType) => {
  return !UNMASKABLE_TYPES.includes(boxType);
};

export const maxHeaderFooterHeight = 300;

export const MASKS_TYPE = {
  HEADER: {
    name: 'en-tete',
    id: 'header',
    boxes: [],
    editableMask: true,
    height: masksDefaultHeight.header,
  },
  FOOTER: {
    name: 'pied de page',
    id: 'footer',
    boxes: [],
    editableMask: true,
    height: masksDefaultHeight.footer,
  },
  // Décor des pages de contenu du PDF, sur toute la page : traits, formes et
  // images posés sous le contenu, l'en-tête et le pied (cf.
  // utils/pageBackground).
  // Créé à sa première édition, depuis l'onglet « Décor de page » des réglages.
  BACKGROUND: {
    name: 'décor de page',
    id: 'background',
    boxes: [],
  },
  SEPARATOR: {
    name: 'séparation de chapitres',
    id: 'break',
    boxes: [],
    editableMask: true,
    hasNotPageNumber: true,
  },
  COVER: {
    name: 'page de garde',
    id: 'cover',
    boxes: [],
    editableMask: true,
    hasNotPageNumber: true,
  },
  END: {
    name: 'page de fin',
    id: 'end',
    boxes: [],
    editableMask: true,
    hasNotPageNumber: true,
  },
  NEWPAGE: {
    name: 'page A4 vierge',
    id: 'newPage',
    boxes: [],
    editableMask: true,
    hasNotPageNumber: true,
  },
  PDFTOIMAGE: {
    name: 'pdf to image',
    id: 'pdfToImage',
    boxes: [],
    editableMask: true,
    hasNotPageNumber: true,
  },
};

export const defaultMasks = [
  { ...MASKS_TYPE.HEADER },
  { ...MASKS_TYPE.FOOTER },
  { ...MASKS_TYPE.SEPARATOR },
  { ...MASKS_TYPE.COVER },
  { ...MASKS_TYPE.END },
  { ...MASKS_TYPE.NEWPAGE },
];

// Plafond des z-index ad hoc utilisés dans l'app (barres flottantes de box
// sélectionnée, AdderMenu, TableConfigDrawer… jusqu'à 99999). L'aperçu plein
// écran d'une image (antd Image) doit toujours passer au-dessus de ces barres,
// pas seulement du contenu qu'il prévisualise.
export const OVERLAY_Z_INDEX_CEILING = 100000;

export { PRIMARY_DARK, PRIMARY_DARK_ADMIN, BORDER_GRAY };
export const VALID_GREEN = '#52c41a';
export const BG_BADGE = '#ffffff';
export const VARIABLE_COLOR = '#D8572A';

export const getPrimaryDark = (isTemplate) =>
  isTemplate ? PRIMARY_DARK_ADMIN : PRIMARY_DARK;

export const PANEL_LIGHT_BG = '#ffffff';

export const MENU_PARENT = {
  name: 'name',
  remark: 'remark',
  content: 'content',
  option: 'option',
  checkbox: 'checkbox',
  header: 'header',
  cell: 'cell',
};

const createAncestorKeys = (parentKeys, prefix, filterKeys = []) => {
  const result = {};
  Object.values(parentKeys).forEach((value) => {
    if (filterKeys.length === 0 || filterKeys.includes(value)) {
      result[value] = `${prefix}_${value}`;
    }
  });
  return result;
};

export const MENU_ANCESTORS = {
  BOX: createAncestorKeys(MENU_PARENT, 'box'),
  CUSTOM_TEXT_VARIABLE: createAncestorKeys(
    MENU_PARENT,
    'custom_text_variable',
    ['remark']
  ),
  VARIABLE_GROUP: createAncestorKeys(MENU_PARENT, 'variable_group', ['remark']),
};
/**
 * EDITOR
 */
export const INLINE_STYLES = [
  { label: GLOSSARY.GRAS, style: 'BOLD' },
  { label: GLOSSARY.ITALIQUE, style: 'ITALIC' },
  { label: GLOSSARY.SOULIGNE, style: 'UNDERLINE' },
];

export const BLOCK_TYPES = [
  { label: GLOSSARY.LISTE_A_PUCE, style: 'unordered-list-item' },
  { label: GLOSSARY.LISTE_NUMEROTEE, style: 'ordered-list-item' },
];

export const FONT_SIZES = [
  { value: 4, display: false },
  { value: 5, display: false },
  { value: 6, display: false },
  { value: 7, display: false },
  { value: 8, display: true },
  { value: 9, display: true },
  { value: 10, display: true },
  { value: 11, display: true },
  { value: 12, display: true },
  { value: 13, display: false },
  { value: 14, display: true },
  { value: 15, display: false },
  { value: 16, display: false },
  { value: 17, display: false },
  { value: 18, display: true },
  { value: 19, display: false },
  { value: 20, display: false },
  { value: 21, display: false },
  { value: 22, display: false },
  { value: 23, display: false },
  { value: 24, display: true },
  { value: 25, display: false },
  { value: 26, display: false },
  { value: 27, display: false },
  { value: 28, display: false },
  { value: 29, display: false },
  { value: 30, display: true },
  { value: 31, display: false },
  { value: 32, display: false },
  { value: 33, display: false },
  { value: 34, display: false },
  { value: 35, display: false },
  { value: 36, display: true },
  { value: 37, display: false },
  { value: 38, display: false },
  { value: 39, display: false },
  { value: 40, display: false },
  { value: 41, display: false },
  { value: 42, display: false },
  { value: 43, display: false },
  { value: 44, display: false },
  { value: 45, display: false },
  { value: 46, display: false },
  { value: 47, display: false },
  { value: 48, display: true },
  { value: 49, display: false },
  { value: 50, display: false },
  { value: 51, display: false },
  { value: 52, display: false },
  { value: 53, display: false },
  { value: 54, display: false },
  { value: 55, display: false },
  { value: 56, display: false },
  { value: 57, display: false },
  { value: 58, display: false },
  { value: 59, display: false },
  { value: 60, display: true },
  { value: 61, display: false },
  { value: 62, display: false },
  { value: 63, display: false },
  { value: 64, display: false },
  { value: 65, display: false },
  { value: 66, display: false },
  { value: 67, display: false },
  { value: 68, display: false },
  { value: 69, display: false },
  { value: 70, display: false },
  { value: 71, display: false },
  { value: 72, display: true },
  { value: 73, display: false },
  { value: 74, display: false },
  { value: 75, display: false },
  { value: 76, display: false },
  { value: 77, display: false },
  { value: 78, display: false },
  { value: 79, display: false },
  { value: 80, display: false },
  { value: 81, display: false },
  { value: 82, display: false },
  { value: 83, display: false },
  { value: 84, display: false },
  { value: 85, display: false },
  { value: 86, display: false },
  { value: 87, display: false },
  { value: 88, display: false },
  { value: 89, display: false },
  { value: 90, display: false },
  { value: 91, display: false },
  { value: 92, display: false },
  { value: 93, display: false },
  { value: 94, display: false },
  { value: 95, display: false },
  { value: 96, display: true },
  { value: 97, display: false },
  { value: 98, display: false },
  { value: 99, display: false },
];

export const generateFontSizesObject = () => {
  return FONT_SIZES.reduce((acc, cur) => {
    return {
      ...acc,
      [`FONT_SIZE_${cur.value}`]: { fontSize: `${cur.value}px` },
    };
  }, {});
};

export const generateHighlightObject = (highlightColors) => {
  return highlightColors.reduce((obj, color) => {
    return {
      ...obj,
      [`HIGHLIGHT_${color.label}`]: { backgroundColor: color.style },
    };
  }, {});
};

export const generateColorsObject = (colors) => {
  return colors.reduce((acc, cur) => {
    return { ...acc, [cur.label]: { color: cur.style } };
  }, {});
};

export const generateFamilyObject = (familyList, fromPdf) => {
  if (!fromPdf) applyFontsOnThePage(familyList);
  return familyList.reduce((acc, cur) => {
    return {
      ...acc,
      [`FONT_FAMILY_${cur.key}`]: { fontFamily: cur.familyName },
    };
  }, {});
};

export const fontsUrlBuilder = (fontsToApply) => {
  let postUrl = 'https://fonts.googleapis.com/css2?';
  let familyList = [];
  fontsToApply.forEach((fontToApply) => {
    familyList.push(
      `family=${fontToApply.familyName?.replace(/\s+/g, '+')}:wght@200;400;700`
    );
  });
  postUrl += familyList.join('&');
  return postUrl;
};

export const addStylesheetURLPreview = (url) => {
  const fontLinks = document
    .getElementsByTagName('head')[0]
    .querySelectorAll('link[href^="https://fonts.googleapis"]');
  fontLinks.forEach((node, index) => {
    if (index > 0) {
      node.remove();
    }
  });
  addStylesheetURL(url);
};

export const addStylesheetURL = (url) => {
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.crossOrigin = 'anonymous';
  link.href = url;
  document.getElementsByTagName('head')[0].appendChild(link);
};

const removeAppliedFonts = () => {
  const fontLinks = document
    .getElementsByTagName('head')[0]
    .querySelectorAll('link[href^="https://fonts.googleapis"]');
  fontLinks.forEach((node) => node.remove());
};

export const applyFontsOnThePage = (fontsToApply) => {
  removeAppliedFonts();
  addStylesheetURL(fontsUrlBuilder(fontsToApply));
};

export const getEditorSelectionFontSize = (editorState, defaultSize) => {
  const currentSelectionStyle = editorState.getSelection();
  const inlineStyle = editorState.getCurrentInlineStyle(currentSelectionStyle);
  let fs;
  FONT_SIZES.forEach((size) => {
    if (inlineStyle.has('FONT_SIZE_' + size)) {
      fs = size;
    }
  });
  return fs || defaultSize;
};

export const letterMaj = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];

export const letterMin = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
];

export const romanNumber = [
  '',
  'C',
  'CC',
  'CCC',
  'CD',
  'D',
  'DC',
  'DCC',
  'DCCC',
  'CM',
  '',
  'X',
  'XX',
  'XXX',
  'XL',
  'L',
  'LX',
  'LXX',
  'LXXX',
  'XC',
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
];

/* Use this as data-role on an ELEMENT if you want to unselect Box when you click on this ELEMENT
   Unselect box logic is in InfinitePage.jsx
*/
export const pageBackground = 'pageBackground';

export const containerBackground = 'containerBackground';

export const roundButtonWidth = 30;

// Un clic dans le canvas vise-t-il une box, ou le vide autour d'elle ? Les deux
// n'ont pas la même cible : la box pour l'un, la structure (container, cellule)
// pour l'autre. Partagé pour que ces lectures ne divergent jamais.
export const BOX_CLICK_SELECTOR =
  '[id^="box-"], #draggable-box, [role="Box"], [role="DraggableBox"], #ComponentPortal, #resizable-box';

export const borderColumnColor = '#8ad2ff87';

export const borderColumnErrorColor = '#ffc2c1';

export const borderColumnWidth = 4;

export const contentHeight = window.innerHeight - navbarHeight - contentPadding;

export const minContainerHeight = 1.5715 * 12 * 3;

export const columnsDispositions = [
  {
    key: 0,
    title: '1 colonne',
    dispositions: [
      { key: 0, sizes: [1] },
      { key: 1, sizes: [1], isDraw: true },
    ],
  },
  {
    key: 1,
    title: '2 colonnes',
    dispositions: [
      { key: 0, sizes: [1 / 2, 1 / 2] },
      { key: 1, sizes: [2 / 3, 1 / 3] },
      { key: 2, sizes: [1 / 3, 2 / 3] },
      { key: 3, sizes: [3 / 4, 1 / 4] },
      { key: 4, sizes: [1 / 4, 3 / 4] },
    ],
  },
  {
    key: 2,
    title: '3 colonnes',
    dispositions: [
      { key: 0, sizes: [1 / 3, 1 / 3, 1 / 3] },
      { key: 1, sizes: [2 / 4, 1 / 4, 1 / 4] },
      { key: 2, sizes: [1 / 4, 2 / 4, 1 / 4] },
      { key: 3, sizes: [1 / 4, 1 / 4, 2 / 4] },
    ],
  },
  {
    key: 3,
    title: '4 colonnes',
    dispositions: [{ key: 0, sizes: [1 / 4, 1 / 4, 1 / 4, 1 / 4] }],
  },
];

export const borderIcons = {
  top: <BorderTopOutlined />,
  bottom: <BorderBottomOutlined />,
  left: <BorderLeftOutlined />,
  right: <BorderRightOutlined />,
};

export const antdFormError = '#ff4d4f';

export const collapseConfigPoint = 1400;

export const viewerTypes = {
  VARIABLES: 'variables',
  PAGENUMBER: 'pageNumber',
  DMZ: 'dmz',
  PREVIEW: 'preview',
};

export const variableHandlingType = {
  VARIABLE: 'variable',
  FORM: 'form',
};

export const tableHeaderSecondaryBgColor = 'rgba(11, 34, 57, 0.06)';
export const tableColor = PRIMARY_DARK;

export const dispostionButtons = {
  oneColumn: {
    icon: (
      <span className='fa-layers' style={{ width: '21px' }}>
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20 }}
        />
      </span>
    ),
    disposition: checkboxDispositions['ONE_COLUMN'],
  },
  twoColumns: {
    icon: (
      <span className='fa-layers' style={{ width: '21px' }}>
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20, position: 'absolute', right: '6px' }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20, position: 'absolute', left: '6px' }}
        />
      </span>
    ),
    disposition: checkboxDispositions['TWO_COLUMNS'],
  },
  threeColumns: {
    icon: (
      <span className='fa-layers' style={{ width: '21px' }}>
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20, position: 'absolute', right: '12px' }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20 }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 20, position: 'absolute', left: '12px' }}
        />
      </span>
    ),
    disposition: checkboxDispositions['THREE_COLUMNS'],
  },
  fourColumns: {
    icon: (
      <span
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          width: '21px',
        }}
      >
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 15 }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 15 }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 15 }}
        />
        <FontAwesomeIcon
          icon={icon({
            name: 'ellipsis-stroke-vertical',
            family: 'sharp',
            style: 'light',
          })}
          style={{ fontSize: 15 }}
        />
      </span>
    ),
    disposition: checkboxDispositions['FOUR_COLUMNS'],
  },
};
